import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Wrench, Shield, Clock, Award, Paintbrush, Hammer, Disc, Car, Sparkles, Droplet, Phone, CheckCircle, Palette, DollarSign, BadgeCheck, Headphones } from 'lucide-react';
import BeforeAfterGallery from '../components/BeforeAfterGallery';
import WorkGallery from '../components/WorkGallery';
import Reviews from '../components/Reviews';

const Home = () => {
  // Before and After image pairs
  const beforeAfterPairs = [
    { before: '/siteimages/before 1.jpg', after: '/siteimages/after 1.jpg' },
    { before: '/siteimages/before 2.jpg', after: '/siteimages/after 2.jpg' },
    { before: '/siteimages/before 3.jpg', after: '/siteimages/after 3.jpg' },
    { before: '/siteimages/before 4.jpg', after: '/siteimages/after 4.jpg' },
    { before: '/siteimages/before 5.jpg', after: '/siteimages/after 5.jpg' },
    { before: '/siteimages/before 6.jpg', after: '/siteimages/after 6.jpg' },
    { before: '/siteimages/before 7.jpg', after: '/siteimages/after 7.jpg' },
    { before: '/siteimages/before 8.jpg', after: '/siteimages/after 8.jpg' },
  ];

  // General work images
  const workImages = [
    '/siteimages/image_0001.jpg',
    '/siteimages/image_0012.jpg',
    '/siteimages/image_0017.jpeg',
    '/siteimages/image_0020.jpg',
    '/siteimages/image_0023.jpg',
    '/siteimages/image_0025.jpg',
    '/siteimages/image_0027.jpg',
    '/siteimages/image_0028.jpg',
    '/siteimages/image_0029.jpg',
    '/siteimages/image_0032.jpg',
    '/siteimages/image_0033.jpg',
    '/siteimages/image_0037.jpg',
  ];

  const services = [
    {
      icon: <Paintbrush className="w-12 h-12" />,
      title: 'Scratch & Paint Repair',
      description: 'Expert color-matched paint repair for scratches, scuffs, and paint damage. Factory-finish quality guaranteed.',
      link: '/services/scratch-paint-repair',
    },
    {
      icon: <Hammer className="w-12 h-12" />,
      title: 'Dent Repair & Removal',
      description: 'Professional dent removal using advanced techniques. We restore your vehicle to its original condition.',
      link: '/services/dent-repair',
    },
    {
      icon: <Disc className="w-12 h-12" />,
      title: 'Alloy Wheel Repair',
      description: 'Comprehensive alloy wheel refurbishment. From minor scuffs to major damage, we handle it all.',
      link: '/services/alloy-wheel-repair',
    },
    {
      icon: <Car className="w-12 h-12" />,
      title: 'Bumper Repair',
      description: 'Complete bumper repair and restoration. Cracks, scratches, and dents expertly repaired.',
      link: '/services/bumper-repair',
    },
    {
      icon: <Sparkles className="w-12 h-12" />,
      title: 'Ceramic Coating',
      description: 'Premium ceramic coating for long-lasting paint protection and showroom shine.',
      link: '/services/ceramic-coating',
    },
    {
      icon: <Droplet className="w-12 h-12" />,
      title: 'Stone Chip & Touch Up',
      description: 'Precision stone chip repair and touch-up services to maintain your vehicle\'s appearance.',
      link: '/services/stone-chip-touch-up',
    },
  ];

  const features = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Guaranteed Quality',
      description: 'Every repair comes with our workmanship guarantee',
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Fast Turnaround',
      description: 'Quick and efficient service without compromising quality',
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Expert Technicians',
      description: 'Highly trained professionals with years of experience',
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: 'Advanced Equipment',
      description: 'State-of-the-art tools and technology for perfect results',
    },
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState([false, false, false]);
  
  const heroImages = [
    '/hero images/black-mechanic-in-car-repair-shop-2025-03-17-08-24-08-utc.JPG',
    '/hero images/dealership-auto-service-job-2025-03-13-23-50-12-utc.jpg',
    '/hero images/female-chinese-mechanic-lifting-tire-in-auto-repai-2025-03-08-19-14-25-utc.jpg',
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const handleImageLoad = (index) => {
    setImagesLoaded(prev => {
      const newState = [...prev];
      newState[index] = true;
      return newState;
    });
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] sm:min-h-[75vh] lg:min-h-[85vh] flex items-center overflow-hidden">
        {/* Background Image Slider */}
        <div className="absolute inset-0 bg-dark-navy">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {/* Blur placeholder while loading */}
              {!imagesLoaded[index] && (
                <div className="absolute inset-0 bg-gradient-to-br from-dark-navy to-dark-teal animate-pulse"></div>
              )}
              
              <img
                src={image}
                alt="Car repair work"
                loading={index === 0 ? 'eager' : 'lazy'}
                onLoad={() => handleImageLoad(index)}
                className={`w-full h-full object-cover transition-opacity duration-500 ${
                  imagesLoaded[index] ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ imageRendering: 'auto' }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-dark-navy/95 via-dark-navy/80 to-dark-navy/60"></div>
            </div>
          ))}
        </div>

        {/* Animated Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(69, 153, 254, 0.1) 10px, rgba(69, 153, 254, 0.1) 20px)'
          }}></div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 sm:px-6 relative z-10 py-8 sm:py-0">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-bmw-blue/20 backdrop-blur-sm border border-bmw-blue/30 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-6">
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-bmw-blue" />
                <span className="text-xs sm:text-sm font-semibold text-bmw-blue">Trusted by 1000+ Customers</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-heading font-bold mb-4 sm:mb-6 leading-tight">
                <span className="block">Professional</span>
                <span className="block text-bmw-blue">Car Body Repair</span>
                <span className="block">& Painting</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-soft-grey mb-6 sm:mb-8 max-w-xl leading-relaxed">
                Expert denting, painting, and body repair services in Leicestershire. 
                Color-matched perfection with guaranteed results.
              </p>

              {/* Features List */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-bmw-blue/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-bmw-blue" />
                  </div>
                  <span className="text-xs sm:text-sm font-medium">Perfect Color Match</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-bmw-blue/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-bmw-blue" />
                  </div>
                  <span className="text-xs sm:text-sm font-medium">Fast Turnaround</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-bmw-blue/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-bmw-blue" />
                  </div>
                  <span className="text-xs sm:text-sm font-medium">Quality Guarantee</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-bmw-blue/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-bmw-blue" />
                  </div>
                  <span className="text-xs sm:text-sm font-medium">Expert Technicians</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link
                  to="/contact"
                  className="bg-racing-red text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-bold hover:bg-red-700 transition-all shadow-2xl hover:shadow-racing-red/50 inline-flex items-center justify-center gap-2 group transform hover:scale-105"
                >
                  Get Free Quote
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="tel:08000452446"
                  className="bg-white/10 backdrop-blur-sm border-2 border-bmw-blue text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-bold hover:bg-bmw-blue transition-all inline-flex items-center justify-center gap-2 group"
                >
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden xs:inline sm:inline">0800 045 2446</span>
                  <span className="xs:hidden sm:hidden">Call Now</span>
                </a>
              </div>
            </div>

            {/* Right Content - Stats Cards */}
            <div className="hidden lg:grid grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all transform hover:scale-105">
                <div className="w-14 h-14 rounded-full bg-bmw-blue flex items-center justify-center mb-4">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-2">100%</h3>
                <p className="text-soft-grey">Quality Guaranteed</p>
              </div>

              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all transform hover:scale-105 mt-8">
                <div className="w-14 h-14 rounded-full bg-bmw-blue flex items-center justify-center mb-4">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-2">24-72h</h3>
                <p className="text-soft-grey">Average Turnaround</p>
              </div>

              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all transform hover:scale-105">
                <div className="w-14 h-14 rounded-full bg-bmw-blue flex items-center justify-center mb-4">
                  <Award className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-2">15+</h3>
                <p className="text-soft-grey">Years Experience</p>
              </div>

              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all transform hover:scale-105 mt-8">
                <div className="w-14 h-14 rounded-full bg-bmw-blue flex items-center justify-center mb-4">
                  <Wrench className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-2">1000+</h3>
                <p className="text-soft-grey">Happy Customers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Slider Indicators */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`h-1 rounded-full transition-all ${
                index === currentImageIndex ? 'w-6 sm:w-8 bg-bmw-blue' : 'w-1 bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue text-white rounded-full mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-heading font-bold text-dark-navy mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-off-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-dark-navy mb-4">
              Our <span className="text-bmw-blue">Services</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive car body repair and painting services with expert technicians and guaranteed quality
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 group hover:-translate-y-2"
              >
                <div className="text-bmw-blue mb-4 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-heading font-bold text-dark-navy mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <Link
                  to={service.link}
                  className="inline-flex items-center gap-2 text-bmw-blue font-semibold hover:gap-4 transition-all"
                >
                  Learn More
                  <ArrowRight size={18} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-gradient-to-br from-dark-navy via-dark-teal to-dark-navy relative overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(69, 153, 254, 0.3) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-bmw-blue/10 border border-bmw-blue/30 rounded-full px-4 py-2 mb-4">
              <Award className="w-4 h-4 text-bmw-blue" />
              <span className="text-sm font-semibold text-bmw-blue">Why Choose Us</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-white mb-6">
              Why Choose <span className="text-bmw-blue">GBW Bodyworks</span>?
            </h2>
            <p className="text-xl text-soft-grey max-w-3xl mx-auto leading-relaxed">
              We combine years of expertise with state-of-the-art equipment to deliver exceptional results that exceed expectations
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {/* Card 1 */}
            <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-bmw-blue/50 transition-all duration-300 hover:transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-bmw-blue to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-bmw-blue/20">
                <Palette className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-bmw-blue transition-colors">Perfect Color Match</h3>
              <p className="text-soft-grey leading-relaxed">
                Advanced color-matching technology ensures your vehicle's paint perfectly matches the original factory finish.
              </p>
            </div>

            {/* Card 2 */}
            <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-bmw-blue/50 transition-all duration-300 hover:transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-bmw-blue to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-bmw-blue/20">
                <DollarSign className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-bmw-blue transition-colors">Competitive Pricing</h3>
              <p className="text-soft-grey leading-relaxed">
                Quality repairs at fair prices with transparent quotes and no hidden fees. Value that respects your budget.
              </p>
            </div>

            {/* Card 3 */}
            <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-bmw-blue/50 transition-all duration-300 hover:transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-bmw-blue to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-bmw-blue/20">
                <BadgeCheck className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-bmw-blue transition-colors">Quality Guarantee</h3>
              <p className="text-soft-grey leading-relaxed">
                We stand behind our work with comprehensive guarantees on all repairs and painting services.
              </p>
            </div>

            {/* Card 4 */}
            <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-bmw-blue/50 transition-all duration-300 hover:transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-bmw-blue to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-bmw-blue/20">
                <Headphones className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-bmw-blue transition-colors">Professional Service</h3>
              <p className="text-soft-grey leading-relaxed">
                From initial assessment to final delivery, we provide professional service every step of the way.
              </p>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-bmw-blue rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-white font-bold">Ready to get started?</p>
                  <p className="text-soft-grey text-sm">Get your free quote today</p>
                </div>
              </div>
              <Link
                to="/contact"
                className="bg-racing-red text-white px-8 py-3 rounded-lg font-bold hover:bg-red-700 transition-all shadow-lg hover:shadow-racing-red/50 inline-flex items-center gap-2 group"
              >
                Contact Us Now
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After Gallery */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <p className="text-bmw-blue font-semibold text-sm uppercase tracking-wide mb-2">Proven Results</p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-dark-navy mb-4">
              Before & <span className="text-bmw-blue">After</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-12">
              See the exceptional quality of our work. Hover over images to see the transformation.
            </p>
          </div>
          <BeforeAfterGallery beforeAfterPairs={beforeAfterPairs} title="" />
        </div>
      </section>

      {/* Work Gallery */}
      <section className="py-20 bg-off-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <p className="text-bmw-blue font-semibold text-sm uppercase tracking-wide mb-2">Portfolio</p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-dark-navy mb-4">
              Our Recent <span className="text-bmw-blue">Projects</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-12">
              Browse our latest bodywork repairs and paint restoration projects.
            </p>
          </div>
          <WorkGallery images={workImages} title="" />
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <p className="text-bmw-blue font-semibold text-sm uppercase tracking-wide mb-2">Customer Feedback</p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-dark-navy mb-4">
              What Our <span className="text-bmw-blue">Customers Say</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-12">
              Don't just take our word for it. Read genuine reviews from our satisfied customers.
            </p>
          </div>
          <Reviews limit={6} showTrustpilot={true} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-bmw-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold mb-6">
            Ready to Restore Your Vehicle?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Contact us today for a free quote. Our expert team is ready to discuss your requirements and provide professional advice.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-racing-red text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition shadow-lg inline-flex items-center justify-center gap-2"
            >
              Get Your Free Quote
              <ArrowRight />
            </Link>
            <a
              href="tel:08000452446"
              className="bg-white text-bmw-blue px-8 py-4 rounded-lg font-semibold hover:bg-off-white transition shadow-lg inline-flex items-center justify-center"
            >
              0800 045 2446
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
